import { PermissionAccessLevel } from './PermissionAccessLevel';
import { User } from './User';

/**
 * This class represents a high level permission that can be used in association with the privilege directive to
 * control access to elements in the dom.  The definitions are (currently) kept in the /assets/groups/groupConfig.json file.
 *
 * The name property is the top level name to be referenced with the directive, such as:
 *
 * <li *mgPrivilege="OVERRIDES">
 *
 * Within each grouping are multiple, individual permissions that are associated with different access levels.  They tie cred
 * groups to graduated access levels within each Permission.  For example, the following json object:
 *	{
 *		"permission": "OVERRIDES",
 *		"accessLevels": [
 *			{
 *				"groupName": "identity_store_group_name_for_OVERRIDES.READ",
 *				"label": "READ",
 *				"accessLevel": 1
 *			},
 *			{
 *				"groupName": "identity_store_group_name_for_OVERRIDES.UPDATE",
 *				"label": "UPDATE",
 *				"accessLevel": 2
 *			},
 *			{
 *				"groupName": "identity_store_group_name_for_OVERRIDES.ADMIN",
 *				"label": "ADMIN",
 *				"accessLevel": 3
 *			}
 *		]
 *	}
 *
 * ..represents a grouping with 3 different access levels.  The groupName property of each group is the name of the credential store's group. The label is
 * the string that will be used in the privilege directive when an elevated access level is specified (<li ngPrivilege="OVERRIDES.UPDATE">),
 * and the accessLevel property is used to also allow access to groups with a smaller accessLevel value (accessLevel ADMIN (3) is authorized to
 * access privileges that specify UPDATE(2) or READ(1) (in the above example)).  Labels and access levels are completed customizable and configurable.
 * If two groups are specified with the same access levels, they are NOT interchangable.  The access level must be greater than the related entry's
 * access level in order to be tenured in.
 */

export class Permission {
	private _name: string;
	private _credGroupNameMap: Map<string, PermissionAccessLevel> = new Map<string, PermissionAccessLevel>();
	private _labelMap: Map<string, PermissionAccessLevel> = new Map<string, PermissionAccessLevel>();
	private _highestAccessLevel: PermissionAccessLevel | null = null;

	constructor(permissionObj: any, envName: string) {
		this._name = permissionObj.permission;
		permissionObj.accessLevels.forEach(
			(accessLevel: any) => {
				const permission = new PermissionAccessLevel(this._name, accessLevel, envName);
				this._credGroupNameMap.set(permission.groupName, permission);
				this._labelMap.set(permission.label, permission);
				if (!this._highestAccessLevel || permission.accessLevel > this._highestAccessLevel.accessLevel) {
					this._highestAccessLevel = permission;
				}
			}
		);
	}

	public getUserAccessLevel(user: User): PermissionAccessLevel | null {
		let userAccess: PermissionAccessLevel | null = null;

		if (user.isAdmin()) {
			userAccess = this._highestAccessLevel;
		} else if (user.groups && user.groups.length > 0) {
			const userGroups: string[] = user.groups;
			for (const credGroupName of userGroups) {
				const permissionAccess: PermissionAccessLevel = this._credGroupNameMap.get(credGroupName);
				if (permissionAccess) {
					if (!userAccess || permissionAccess.accessLevel > userAccess.accessLevel) {
						userAccess = permissionAccess;
					}
				}
			}
		}
		return userAccess;
	}

	public get permissions(): PermissionAccessLevel[] {
		return [...this._credGroupNameMap.values()].sort(
			(i, j) => {
				if (i.accessLevel > j.accessLevel) {
					return -1;
				}
				if (i.accessLevel < j.accessLevel) {
					return 1;
				}
				if (i.label.toLowerCase() > j.label.toLowerCase()) {
					return -1;
				}
				if (i.label.toLowerCase() < j.label.toLowerCase()) {
					return 1;
				}
				return 0;
			}
		);
	}

	public permitted(credGroup: string, requiredLabel: string, checkParents: boolean): boolean {

		let allowed = false;

		const usersPermission: PermissionAccessLevel = this._credGroupNameMap.get(credGroup);
		if (usersPermission) {
			if (requiredLabel) {
				const requiredPermission = this._labelMap.get(requiredLabel);
				if (requiredPermission) {
					if (requiredPermission.groupName === credGroup) {
						allowed = true;
					} else {
						if (usersPermission.accessLevel > requiredPermission.accessLevel && checkParents) {
							allowed = true;
						}
					}
				}
			} else {
				allowed = true;
			}
		}
		return allowed;
	}

	get name(): string {
		return this._name;
	}

	set name(newName: string) {
		this._name = newName;
	}
}
